import React, { useCallback, useEffect, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

const UnresolvedRow = ({ item, recipients, onLink }) => {
  const [rid, setRid] = useState('');
  return (
    <div className="flex flex-wrap items-center gap-2 py-2 border-b text-sm">
      <span className="font-medium">{item.name}</span>
      <span className="text-xs text-red-600">{item.problem}</span>
      <select value={rid} onChange={(e) => setRid(e.target.value)} className="border rounded-md px-2 py-1 text-xs max-w-[200px]"
        data-testid={`ignore-unresolved-select-${item.name}`}>
        <option value="">Pick Mercury recipient…</option>
        {recipients.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
      </select>
      <button type="button" disabled={!rid} onClick={() => onLink(item, recipients.find((r) => r.id === rid))}
        className="text-xs text-blue-600 disabled:opacity-40">Link</button>
    </div>
  );
};

export const IgnoreListDialog = ({ open, onClose, apiFetch, apiUrl }) => {
  const [data, setData] = useState({ entries: [], unresolved: [] });
  const [recipients, setRecipients] = useState([]);
  const base = `${apiUrl}/api/billpay/payments/ignore-list`;

  const load = useCallback(async () => {
    const resp = await apiFetch(base);
    if (resp.ok) setData(await resp.json());
  }, [apiFetch, base]);

  useEffect(() => {
    if (!open) return;
    load();
    apiFetch(`${apiUrl}/api/billpay/mercury/recipients`).then((r) => r.ok && r.json()).then((d) => d && setRecipients(d.recipients || []));
  }, [open, load, apiFetch, apiUrl]);

  const remove = async (rid) => {
    const resp = await apiFetch(`${base}/${rid}`, { method: 'DELETE' });
    if (resp.ok) setData((d) => ({ ...d, entries: d.entries.filter((e) => e.recipient_id !== rid) }));
  };

  const link = async (item, r) => {
    const resp = await apiFetch(base, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ recipient_id: r.id, recipient_name: r.name, name: item.name, reason: item.reason, seed_name: item.name }),
    });
    if (resp.ok) setData(await resp.json());
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="ignore-list-dialog" className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Settings: ignored payees</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Payments to these Mercury recipients are saved as Ignored, unless an open QBO bill matches the amount and invoice #.</p>
        {data.entries.length === 0 && <p className="text-sm text-slate-500">No payees on the ignore list.</p>}
        {data.entries.map((e) => (
          <div key={e.recipient_id} className="flex items-start justify-between gap-3 py-2 border-b" data-testid={`ignore-entry-${e.recipient_id}`}>
            <div className="text-sm min-w-0">
              <p className="font-medium">{e.name}{e.reason ? <span className="text-slate-500 font-normal"> · {e.reason}</span> : null}</p>
              <p className="text-xs text-slate-500 break-all">Mercury: {e.recipient_name} ({e.recipient_id})</p>
            </div>
            <button type="button" data-testid={`ignore-entry-remove-${e.recipient_id}`} onClick={() => remove(e.recipient_id)}
              className="text-red-600 hover:text-red-800" title="Remove from ignore list"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {data.unresolved.length > 0 && (
          <div className="mt-2">
            <p className="text-sm font-medium text-slate-800">Not linked yet</p>
            {data.unresolved.map((u) => <UnresolvedRow key={u.name} item={u} recipients={recipients} onLink={link} />)}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
